import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable, asapScheduler, combineLatest, merge } from 'rxjs';
import { distinctUntilChanged, filter, map, observeOn, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { CustomAuthServiceService } from '../../auth/custom-auth-service.service';
import { PermissionsService } from '../../auth/permissions.service';
import { ClientSettingsService } from '../../manageb/client-settings.service';
import { NavigationScreensService } from '../../manageb/navigation-screens.service';
import { isDischargePlanEnabledForClient } from '../../manageb/discharge-plan/discharge-plan-entitlement';
import { AdminNavEntry, getAdminNavItems, isAdminNavEntryVisible, resolveAdminNavRole } from '../../admin/admin-routes.config';
import { environment } from '../../environment/environment';
import {
  BreadcrumbCrumb, NAV_CONFIG, NavSearchEntry, ProductId, ResolvedNavCategory, ResolvedNavNode, TITLES,
  breadcrumbTrailForPath, dropPathlessCrumbs, navSearchEntries, pathOf, productForPath, railCategories,
  railScreens, recentPathOf, resolveNav, settingsScreens, targetForTitle, titlesForRole,
} from '../../navigation';
import { CustomPermissionKey } from '../../models/user';
import { localStorageGet, localStorageSet } from '../../utils';

export type AdminNavItem = AdminNavEntry;

export interface HeaderNotice {
  id: string;
  message: string;
  actionLabel: string;
  action: () => void;
}

const ADMIN_URL_PREFIX = '/manageb/admin';
const RECENTS_KEY = 'nav.recentPaths';
const RAIL_HIDDEN_KEY = 'nav.railHidden';
const RECENTS_LIMIT = 5;
const WELCOME_SEEN_KEY = 'nav.welcomeSeen';
const WELCOME_SEEN_LIMIT = 20;
const NOTICES_DISMISSED_KEY = 'nav.noticesDismissed';

function readStringList(key: string): string[] {
  const stored = localStorageGet(key);
  return Array.isArray(stored) ? stored.filter((item): item is string => typeof item === 'string') : [];
}

/** Stored recents as they are recorded now: a KPI page saved before the KPIs hub reads as the hub, once. */
function readRecents(): string[] {
  return readStringList(RECENTS_KEY).map(recentPathOf).filter((path, index, paths) => paths.indexOf(path) === index);
}

/**
 * The single bridge between Angular state and the framework-free nav model.
 * Do NOT inject UtilsService here: it closes the auth -> nav -> utils -> auth
 * ES-module cycle. Nothing under auth/ may import this file.
 */
@Injectable({ providedIn: 'root' })
export class NavStateService {
  private readonly drawerOpenSubject = new BehaviorSubject<boolean>(false);
  private readonly panelOverlayOpenSubject = new BehaviorSubject<boolean>(false);

  readonly drawerOpen$ = this.drawerOpenSubject.asObservable();
  readonly panelOverlayOpen$ = this.panelOverlayOpenSubject.asObservable();

  // Desktop only: the header toggle collapses the rail; <576px the drawer ignores it.
  private readonly railHiddenSubject = new BehaviorSubject<boolean>(localStorageGet(RAIL_HIDDEN_KEY) === true);
  readonly railHidden$ = this.railHiddenSubject.asObservable();

  // Transient edge-hover peek over a hidden rail. Never persisted: the hidden choice is the preference.
  private readonly railPeekSubject = new BehaviorSubject<boolean>(false);
  readonly railPeek$ = this.railPeekSubject.asObservable();

  readonly railVisible$ = combineLatest([this.railHidden$, this.railPeek$]).pipe(
    map(([hidden, peek]) => !hidden || peek),
    distinctUntilChanged(),
  );

  /** Product the user clicked on the rail; null means "follow the active route". */
  private readonly selectedProductSubject = new BehaviorSubject<ProductId | null>(null);

  private readonly paletteOpenSubject = new BehaviorSubject<boolean>(false);

  private readonly url$: Observable<string> = this.router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    map((event) => event.urlAfterRedirects),
    startWith(this.router.url),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  // The role is PULLED, not pushed: authState$ fires once per login, so impersonation
  // ("Change User" / "Use As SA") would leave the panel on the previous role until a reload.
  // Re-pulling on every navigation mirrors the legacy option-menu, which re-read the role on open.
  readonly resolved$: Observable<ResolvedNavCategory[]> = merge(
    this.auth.authState$,
    this.url$,
    this.clientSettings.settings$,
  ).pipe(
    map(() => this.navFingerprint()),
    distinctUntilChanged(),
    map(() => {
      const role = this.effectiveRole();
      // No usable user: render nothing rather than guess the general menu.
      if (role === null) { return []; }
      return resolveNav(NAV_CONFIG, {
        roleNumber: role,
        can: (capability: string) => this.permissions.has(capability as CustomPermissionKey),
        clientProducts: this.clientSettings.settings.uiSettings.products,
        ctx: this.screens.navCtx(),
      });
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  /** Product tiles only. Admin gets its own tile from adminScreen$; account screens live behind the avatar. */
  readonly railCategories$: Observable<ResolvedNavCategory[]> = this.resolved$.pipe(
    map((categories) => railCategories(categories)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly railScreens$: Observable<ResolvedNavNode[]> = this.resolved$.pipe(
    map((categories) => railScreens(categories)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  // Role 132 has no Profile entry; /client is out of its menu on both nav surfaces.
  readonly profileAllowed$: Observable<boolean> = this.resolved$.pipe(
    map(() => {
      const role = this.effectiveRole();
      return role !== null && titlesForRole(role, this.screens.navCtx()).includes(TITLES.profile);
    }),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly settingsScreens$: Observable<ResolvedNavNode[]> = this.resolved$.pipe(
    map((categories) => settingsScreens(categories)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  // Admin is a rail tile of its own, so the avatar menu must not repeat it.
  readonly settingsMenuScreens$: Observable<ResolvedNavNode[]> = this.settingsScreens$.pipe(
    map((screens) => screens.filter((screen) => screen.title !== TITLES.admin)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  /** Present only when the role's presets include Admin — the gate stays in resolveNav. */
  readonly adminScreen$: Observable<ResolvedNavNode | undefined> = this.settingsScreens$.pipe(
    map((screens) => screens.find((screen) => screen.title === TITLES.admin)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly searchEntries$: Observable<NavSearchEntry[]> = this.resolved$.pipe(
    map((categories) => navSearchEntries(categories)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly currentPath$: Observable<string> = this.url$.pipe(
    map((url) => url.split('?')[0].split('#')[0]),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly adminMode$: Observable<boolean> = this.url$.pipe(
    map((url) => url.split('?')[0].startsWith(ADMIN_URL_PREFIX)),
    distinctUntilChanged(),
  );

  // Never seeded from the url: a reload on an admin page must land on the product menu, not the admin one.
  /** Clicking an Admin entry picks the admin menu; a rail/tab product click takes it back. */
  private readonly adminSelectedSubject = new BehaviorSubject<boolean>(false);

  /** What the panel shows: the admin menu while Admin was the last tile picked; leaving admin urls drops it. */
  readonly panelAdmin$: Observable<boolean> = this.adminSelectedSubject.pipe(distinctUntilChanged());

  /** Rail highlight: the rail click wins until the route moves to a different product. */
  readonly activeProduct$: Observable<ProductId | undefined> = combineLatest([
    this.railCategories$,
    this.resolved$,
    this.url$,
    this.selectedProductSubject,
  ]).pipe(
    map(([rail, categories, url, selected]) => {
      const available = new Set(rail.map((c) => c.product));
      if (selected && available.has(selected)) { return selected; }
      // Settings/admin routes belong to no product tile — hold the last product instead of blanking.
      const fromRoute = productForPath(categories, url);
      if (fromRoute && available.has(fromRoute)) { return fromRoute; }
      return rail[0]?.product;
    }),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly breadcrumbs$: Observable<BreadcrumbCrumb[]> = combineLatest([this.resolved$, this.url$]).pipe(
    map(([categories, url]) => this.buildBreadcrumbs(categories, url)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  /** Detail pages name their record (e.g. the patient) instead of the generic leaf crumb. */
  private readonly pageTitleOverrideSubject = new BehaviorSubject<string>('');

  readonly pageTitle$: Observable<string> = combineLatest([this.breadcrumbs$, this.pageTitleOverrideSubject]).pipe(
    map(([crumbs, override]) => override || crumbs[crumbs.length - 1]?.title || ''),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly userName$: Observable<string> = this.auth.authState$.pipe(
    map(() => this.userName()),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly userInitials$: Observable<string> = this.userName$.pipe(
    map((name) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0].toUpperCase()).join('')),
  );

  readonly userEmail$: Observable<string> = this.auth.authState$.pipe(
    map(() => this.userEmail()),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly adminItems$: Observable<AdminNavItem[]> = this.auth.authState$.pipe(
    map(() => this.visibleAdminItems()),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  // On staging the env warning replaces the tenant name, matching the legacy header.
  readonly clientName$: Observable<string> = this.clientSettings.settings$.pipe(
    map((settings) => (environment.env === 'staging' ? 'Staging Not Live' : settings.clientName)),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private readonly recentPathsSubject = new BehaviorSubject<string[]>(readRecents());

  /** Most-recent-first, capped at RECENTS_LIMIT. Feeds the command palette's empty state. */
  readonly recentPaths$ = this.recentPathsSubject.asObservable();

  readonly paletteOpen$ = this.paletteOpenSubject.asObservable();

  private readonly headerNoticeSubject = new BehaviorSubject<HeaderNotice | null>(null);
  readonly headerNotice$ = this.headerNoticeSubject.pipe(observeOn(asapScheduler));

  constructor(
    private readonly auth: CustomAuthServiceService,
    private readonly permissions: PermissionsService,
    private readonly router: Router,
    private readonly clientSettings: ClientSettingsService,
    private readonly screens: NavigationScreensService,
  ) {
    // Gate per navigation, before searchEntries$: flag off must not run resolveNav, and the flag can flip after construction.
    this.currentPath$.pipe(
      filter(() => this.enabled),
      switchMap((path) => this.searchEntries$.pipe(map((entries) => ({ path, entries })))),
    ).subscribe(({ path, entries }) => {
      // Only real nav destinations become recents, so deep-linked detail pages don't pollute them; a hub's page counts as its hub.
      const recent = recentPathOf(path);
      if (entries.some((entry) => entry.path === recent)) { this.recordRecent(recent); }
    });
    // A page's title must never outlive its page: leaving drops it back to the leaf crumb.
    this.currentPath$.subscribe(() => this.pageTitleOverrideSubject.next(''));
    this.adminMode$.pipe(filter((onAdminUrl) => !onAdminUrl)).subscribe(() => this.adminSelectedSubject.next(false));
  }

  /** Called by a detail page once its record loads; '' restores the crumb-derived title. */
  setPageTitle(title: string): void {
    this.pageTitleOverrideSubject.next(title || '');
  }

  showHeaderNotice(notice: HeaderNotice): void {
    if (readStringList(NOTICES_DISMISSED_KEY).includes(notice.id)) { return; }
    this.headerNoticeSubject.next(notice);
  }

  clearHeaderNotice(id: string): void {
    if (this.headerNoticeSubject.value?.id === id) { this.headerNoticeSubject.next(null); }
  }

  dismissHeaderNotice(): void {
    const notice = this.headerNoticeSubject.value;
    if (!notice) { return; }
    const dismissed = readStringList(NOTICES_DISMISSED_KEY);
    if (!dismissed.includes(notice.id)) { localStorageSet(NOTICES_DISMISSED_KEY, [...dismissed, notice.id]); }
    this.headerNoticeSubject.next(null);
  }

  private recordRecent(path: string): void {
    const next = [path, ...this.recentPathsSubject.value.filter((p) => p !== path)].slice(0, RECENTS_LIMIT);
    this.recentPathsSubject.next(next);
    localStorageSet(RECENTS_KEY, next);
  }

  setPaletteOpen(open: boolean): void {
    this.paletteOpenSubject.next(open);
  }

  togglePalette(): void {
    this.paletteOpenSubject.next(!this.paletteOpenSubject.value);
  }

  get enabled(): boolean {
    return !!this.clientSettings.settings.uiSettings.newNavigation;
  }

  get panelOverlayOpen(): boolean {
    return this.panelOverlayOpenSubject.value;
  }

  get selectedProduct(): ProductId | null {
    return this.selectedProductSubject.value;
  }

  selectProduct(product: ProductId): void {
    this.selectedProductSubject.next(product);
    this.adminSelectedSubject.next(false);
  }

  /** The only way into the admin menu - clicking the Admin rail tile. Opens the menu without navigating. */
  selectAdmin(): void {
    this.adminSelectedSubject.next(true);
  }

  /** Snapshot for click handlers: is the panel currently showing the admin menu? */
  get panelAdmin(): boolean {
    return this.adminSelectedSubject.value;
  }

  setDrawerOpen(open: boolean): void {
    // The drawer opens on the active product's menu, never rail-only; the rail can still collapse it.
    if (open) { this.panelOverlayOpenSubject.next(true); }
    this.drawerOpenSubject.next(open);
  }

  get railHidden(): boolean {
    return this.railHiddenSubject.value;
  }

  get railPeek(): boolean {
    return this.railPeekSubject.value;
  }

  toggleRailHidden(): void {
    const hidden = !this.railHiddenSubject.value;
    if (hidden) { this.panelOverlayOpenSubject.next(false); }
    this.railPeekSubject.next(false);
    this.railHiddenSubject.next(hidden);
    localStorageSet(RAIL_HIDDEN_KEY, hidden);
  }

  setRailPeek(peek: boolean): void {
    if (peek && !this.railHiddenSubject.value) { return; }
    if (this.railPeekSubject.value === peek) { return; }
    // Ending a peek takes the panel with it, the same way hiding the rail does.
    if (!peek) { this.panelOverlayOpenSubject.next(false); }
    this.railPeekSubject.next(peek);
  }

  // True while the panel owes its open state to a rail hover; a click pins it, closing resets it.
  panelOpenedByHover = false;

  setPanelOverlayOpen(open: boolean): void {
    if (!open) { this.panelOpenedByHover = false; }
    this.panelOverlayOpenSubject.next(open);
  }

  /** Falls back to localStorage the same way the legacy header does — a reload can beat the auth subject. */
  userName(): string {
    const user: any = this.auth.currentUserValue();
    return user?.fullName || localStorage.getItem('fullName') || '';
  }

  /** The login payload carries the email in `user` as a plain string (server sends `user: userName`). */
  userEmail(): string {
    const user: any = this.auth.currentUserValue();
    const value = user?.user;
    return (typeof value === 'string' ? value : value?.email) || '';
  }

  /** No identity or no flag reads as "seen": a card we cannot record would re-pop on every load. */
  welcomeSeen(): boolean {
    const id = this.welcomeIdentity();
    if (!id || !this.enabled) { return true; }
    return readStringList(WELCOME_SEEN_KEY).includes(id);
  }

  markWelcomeSeen(): void {
    const id = this.welcomeIdentity();
    if (!id || !this.enabled) { return; }
    const seen = readStringList(WELCOME_SEEN_KEY);
    if (seen.includes(id)) { return; }
    localStorageSet(WELCOME_SEEN_KEY, [...seen, id].slice(-WELCOME_SEEN_LIMIT));
  }

  // One shared browser profile serves several users, so the seen list is per identity, not a boolean.
  private welcomeIdentity(): string {
    return this.userEmail().trim().toLowerCase();
  }

  roleNumber(): number {
    return this.effectiveRole() ?? NaN;
  }

  /** null = no usable user (expired token, post-logout SPA state) — never a role guess. */
  private effectiveRole(): number | null {
    const user: any = this.auth.currentUserValue();
    if (!user) { return null; }
    const role = Number(user.contactAs ? user.contactAs.roleNumber : user.role);
    return isFinite(role) ? role : null;
  }

  /** Identity of every resolveNav input, so a navigation that changes nothing re-emits nothing. */
  private navFingerprint(): string {
    const ui = this.clientSettings.settings.uiSettings;
    // The Discharge Plan flag reaches resolveNav through navCtx's cache read,
    // so the identity has to read the SAME cache: the settings emission that
    // rewrites it may carry no key at all (a region that never configured the
    // flag), and fingerprinting the raw key would leave a stale entry from the
    // previous session on the rail until the next login.
    return JSON.stringify([
      this.effectiveRole(), ui.products ?? null, ui.newVersionPages ?? null, isDischargePlanEnabledForClient(),
      this.screens.navCtx().gatewayReferralLinksDisabled,
    ]);
  }

  private visibleAdminItems(): AdminNavItem[] {
    const role = resolveAdminNavRole(this.effectiveRole() ?? NaN);
    const hasPermission = (permission: CustomPermissionKey) => this.permissions.has(permission);
    return getAdminNavItems().filter((item) => isAdminNavEntryVisible(item, role, hasPermission));
  }

  private buildBreadcrumbs(categories: ResolvedNavCategory[], url: string): BreadcrumbCrumb[] {
    const path = url.split('?')[0].split('#')[0];
    if (!path.startsWith(ADMIN_URL_PREFIX)) {
      return breadcrumbTrailForPath(categories, path);
    }

    // ADMIN_ROUTES_CONFIG imports Angular component types, so it cannot live in the pure model:
    // compose the Admin root crumb from the model and the leaf from the admin config.
    const trail = breadcrumbTrailForPath(categories, path);
    const root: BreadcrumbCrumb[] = trail.length
      ? trail
      : [{ title: TITLES.admin, path: pathOf(targetForTitle(TITLES.admin, this.roleNumber())) }];
    const leaf = this.visibleAdminItems().find((item) => item.path === path);
    // For a full admin the Admin node already points at the dashboard, so the trail can end on the leaf.
    if (!leaf || root[root.length - 1]?.path === leaf.path) { return root; }
    return dropPathlessCrumbs([...root, { title: leaf.title, path: leaf.path }]);
  }
}
